import { memo } from 'react';
import type { ApiKeyItem } from './adminApiKeys.api';
import AdminNav from '../admin/components/AdminNav';
import './AdminApiKeys.css';

interface Props {
  loading: boolean;
  error: string | null;
  keys: ApiKeyItem[];
  newKeyValue: string | null;
  onCreate: (label: string) => void;
  onRevoke: (id: string) => void;
  onClearNewKey: () => void;
}

const AdminApiKeysPageView = memo(function AdminApiKeysPageView({ loading, error, keys, newKeyValue, onCreate, onRevoke, onClearNewKey }: Props) {
  if (loading) return <main className="admin-apikeys-container" role="status" aria-live="polite">Loading API keys...</main>;
  if (error) return <main className="admin-apikeys-container admin-error-text" role="alert">Error: {error}</main>;

  return (
    <main className="admin-apikeys-container" role="status" aria-live="polite">
      <AdminNav />
      <div className="admin-apikeys-content">
        <h1 className="admin-apikeys-title">API Key Management</h1>

        <form className="admin-apikeys-form" onSubmit={(e) => {
          e.preventDefault();
          const fd = new FormData(e.currentTarget as HTMLFormElement);
          const label = String(fd.get('label') || '');
          if (label) onCreate(label);
          (e.currentTarget as HTMLFormElement).reset();
        }}>
          <label htmlFor="label-input">Label</label>
          <input id="label-input" name="label" type="text" placeholder="Key label" required className="admin-apikeys-input" />
          <button type="submit" className="admin-apikeys-button">Create Key</button>
        </form>

        {newKeyValue && (
          <div className="admin-apikeys-new-key-alert">
            <p><strong>New API Key Created</strong></p>
            <p>Copy this key now. It will not be shown again.</p>
            <div className="admin-apikeys-new-key-value">{newKeyValue}</div>
            <button
              type="button"
              className="admin-apikeys-copy-button"
              onClick={() => { navigator.clipboard.writeText(newKeyValue); }}
            >Copy</button>
            <button
              type="button"
              className="admin-apikeys-dismiss-button"
              onClick={onClearNewKey}
            >Dismiss</button>
          </div>
        )}

        <table className="admin-apikeys-table">
          <thead>
            <tr>
              <th scope="col" className="admin-apikeys-th">Key Prefix</th>
              <th scope="col" className="admin-apikeys-th">Label</th>
              <th scope="col" className="admin-apikeys-th">Status</th>
              <th scope="col" className="admin-apikeys-th">Created</th>
              <th scope="col" className="admin-apikeys-th">Expires</th>
              <th scope="col" className="admin-apikeys-th">Last Used</th>
              <th scope="col" className="admin-apikeys-th">Actions</th>
            </tr>
          </thead>
          <tbody>
            {keys.map(k => (
              <tr key={k._id}>
                <td className="admin-apikeys-td">{k.keyPrefix}</td>
                <td className="admin-apikeys-td">{k.label}</td>
                <td className="admin-apikeys-td">
                  {k.isActive
                    ? <span className="admin-apikeys-status-active">Active</span>
                    : <span className="admin-apikeys-status-revoked">Revoked</span>
                  }
                </td>
                <td className="admin-apikeys-td">{new Date(k.createdAt).toLocaleString()}</td>
                <td className="admin-apikeys-td">{k.expiresAt ? new Date(k.expiresAt).toLocaleString() : '-'}</td>
                <td className="admin-apikeys-td">{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString() : '-'}</td>
                <td className="admin-apikeys-td">
                  {k.isActive && (
                    <button
                      type="button"
                      className="admin-apikeys-revoke-button"
                      onClick={() => onRevoke(k._id)}
                      aria-label={`Revoke key ${k.label}`}
                    >Revoke</button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </main>
  );
});

export default AdminApiKeysPageView;
